
import {DigitizationTools} from "../ToolRegistry.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {CircleAuxiliaryVisual} from "../MeasureAuxiliaryVisuals.js";
import {createFeature} from "../Feature.js";
import {captureThreePointsWithPreview} from "./shapeCaptureHelpers.js";
import {circumcircle, circleMetrics} from "../geometry/measurementGeometry.js";
import {formatLength} from "./distanceTool.js";
import {formatArea} from "./areaTool.js";

import {T, t} from "../i18n/index.js";
// Mål cirkel: kopieret fra det gamle native "Circle"-måleværktøj og genskrevet
// til Digitaliserings Målinger-format. De tre punkter ligger PÅ cirklens
// periferi (cirklen gennem dem beregnes), og målingen er en rigtig,
// redigerbar 3-punkts LineString - flyttes et punkt, tegnes cirklen og alle
// tal om. Resultat-labelen viser radius, diameter, omkreds, areal og centrum
// (det gamle værktøjs info-tabel: centrum XYZ, radius, omkreds - plus
// diameter og areal efter ønske). Forskellig fra tegneværktøjet "Cirkel"
// (centrum + radius, registrerer en polygon): dette MÅLER blot en cirkel.
export const CircleMeasureTool = {
	id: "digitize_measure_circle",
	name: T("Mål cirkel"),
	category: "measurement",
	kind: "measurement",
	toolbarGroup: "measurement",
	toolbarOrder: 70,
	icon: "measure_circle.svg",
	resultGeometryType: "LineString",
	fixedVertexCount: 3,
	tooltip: T("Mål en cirkel gennem tre punkter på dens kant: radius, diameter, omkreds, areal og centrum."),
	instructions: T("Venstreklik for at vælge tre punkter på cirklens kant."),

	customCapture(dm, tool){
		runCircleMeasureCapture(dm, tool);
	},
	// Restore extension point (see DigitizationModule._buildLoadedFeature): the finished measurement keeps its result label and circle.
	decorateLoaded(visual){ applyCircleMeasureRepresentation(visual); },
};

DigitizationTools.register(CircleMeasureTool);

const STEP_INSTRUCTIONS = [
	null,
	T("Venstreklik for at vælge det første punkt på cirklens kant."),
	T("Venstreklik for at vælge det andet punkt på cirklens kant."),
	T("Venstreklik for at vælge det tredje punkt på cirklens kant."),
];

function runCircleMeasureCapture(dm, tool){
	captureThreePointsWithPreview(dm, tool, (n) => (STEP_INSTRUCTIONS[n] ? t(STEP_INSTRUCTIONS[n]) : null)).then((points) => {
		if(!points) return; // ESC/skift af værktøj
		finalizeCircleMeasure(dm, tool, points);

		if(dm.activeTool === tool) dm.startInsertion(tool);
	});
}

function summaryLines(raw, visual){
	if(raw.length < 3) return null;
	const circle = circumcircle(raw[0], raw[1], raw[2]);
	if(!circle) return null; // kollineære punkter: ingen cirkel
	const m = circleMetrics(circle);
	const [cx, cy, cz] = circle.center;
	return [
		visual.name,
		`Radius: ${formatLength(m.radius)}`,
		`Diameter: ${formatLength(m.diameter)}`,
		t("Omkreds: {0}", formatLength(m.circumference)),
		t("Areal: {0}", formatArea(m.area)),
		t("Centrum: {0}, {1}, {2}", cx.toFixed(3), cy.toFixed(3), cz.toFixed(3)),
	];
}

// På modsat side af centrum end radiuslinjen/radius-labelen (som går mod
// punkt 2), så de to labels ikke ligger oven på hinanden.
function summaryAnchor(raw){
	const circle = circumcircle(raw[0], raw[1], raw[2]);
	if(!circle) return {x: raw[1][0], y: raw[1][1], z: raw[1][2]};
	const [cx, cy, cz] = circle.center;
	return {x: cx + (cx - raw[1][0]) * 0.5, y: cy + (cy - raw[1][1]) * 0.5, z: cz + (cz - raw[1][2]) * 0.5};
}

// The finished circle measurement: hidden vertex labels, the result label and the fitted circle, kept in step with the geometry. Shared by
// the capture flow and the restore hook (decorateLoaded).
function applyCircleMeasureRepresentation(visual){
	visual.setVertexLabelsSuppressed(true);
	visual.enableSummaryLabel(summaryLines, summaryAnchor);

	const auxiliary = new CircleAuxiliaryVisual();
	visual.add(auxiliary);
	visual.userData.auxiliaryVisual = auxiliary;
	const refresh = () => {
		const [a, b, c] = visual.points;
		auxiliary.updateFromPoints(a && a.position, b && b.position, c && c.position);
	};
	refresh();
	visual.addEventListener("geometry_changed", refresh);
}

function finalizeCircleMeasure(dm, tool, points){
	const visual = new FeatureVisual("LineString");
	for(const p of points) visual.appendAtEnd(p);
	applyCircleMeasureRepresentation(visual);

	const feature = createFeature({
		geometryType: "LineString",
		coordinates: visual.toCoordinates(),
		category: tool.category,
		tool: tool.id,
		featureType: tool.featureType || tool.name,
		name: `${tool.name} ${dm.featureManager.features.length + 1}`,
	});

	visual.setInfo(feature.properties.name, feature.properties.description);
	dm.scene.add(visual);
	dm.registerNewFeature(feature, visual);
}
